import { useEffect, useState } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClientDocument,
  getSignedDocumentUrl,
  listVisibleDocumentsForEmail,
} from "@/services/clientDocumentsService";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  email: string;
}

type UnifiedDoc = {
  id: string;
  name: string;
  type: "client_document" | "contract";
  meta?: string;
  file_path?: string;
  public_url?: string;
};

const UserClientDocuments = ({ email }: Props) => {
  const [items, setItems] = useState<UnifiedDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        // 1) Uploaded client documents flagged visible
        const docsPromise = listVisibleDocumentsForEmail(email);

        // 2) Signed contract PDFs linked to this email
        const contractsPromise = supabase
          .from("contracts")
          .select("id, pdf_path, signed_at, contract_type, reservations!inner(email, first_name, last_name)")
          .eq("status", "signed")
          .not("pdf_path", "is", null)
          .ilike("reservations.email", email);

        const [docs, contracts] = await Promise.all([docsPromise, contractsPromise]);

        const merged: UnifiedDoc[] = [
          ...(docs || []).map((d: ClientDocument) => ({
            id: `doc_${d.id}`,
            name: d.file_name,
            type: "client_document" as const,
            meta: new Date(d.created_at).toLocaleDateString("fr-FR"),
            file_path: d.file_path,
          })),
          ...((contracts.data || []) as any[]).map((c) => ({
            id: `contract_${c.id}`,
            name: `${c.contract_type === "lease" ? "Contrat de location" : "Règlement intérieur"} signé.pdf`,
            type: "contract" as const,
            meta: c.signed_at ? `Signé le ${new Date(c.signed_at).toLocaleDateString("fr-FR")}` : undefined,
            public_url: `https://kxrduwrurqjvouiqobyc.supabase.co/storage/v1/object/public/generated-contracts/${c.pdf_path}`,
          })),
        ];

        if (ok) setItems(merged);
      } catch (e) {
        console.error(e);
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [email]);

  const open = async (item: UnifiedDoc) => {
    try {
      if (item.public_url) {
        window.open(item.public_url, "_blank");
        return;
      }
      if (item.file_path) {
        const url = await getSignedDocumentUrl(item.file_path, 300);
        window.open(url, "_blank");
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement des documents…
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="rounded-lg border bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-5 w-5 text-villa-turquoise" />
        <h3 className="font-medium text-villa-blue">Documents vus précédemment</h3>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between p-3 bg-villa-sand/20 border rounded-md"
          >
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm truncate">{item.name}</span>
                  {item.type === "contract" && (
                    <Badge variant="outline" className="text-xs">Contrat signé</Badge>
                  )}
                </div>
                {item.meta && (
                  <p className="text-xs text-muted-foreground">{item.meta}</p>
                )}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => open(item)}>
              <Download className="h-4 w-4 mr-1" /> Ouvrir
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserClientDocuments;
